"use client";

import { SalesAgentApplicationSchema, SalesAgentApplicationType } from "@/lib/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { startTransition, useEffect, useState, Children, useRef, useLayoutEffect, ReactNode } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUpload } from "@/components/molecules/file-upload";
import statesData from "@/lib/data/state.json";
import { motion, AnimatePresence, Variants } from "motion/react";

type StateData = {
  name: string;
  lgas: string[];
};

// Stepper Components
interface StepProps {
  children: ReactNode;
}

function Step({ children }: StepProps) {
  return <div className='space-y-4'>{children}</div>;
}

interface StepIndicatorProps {
  step: number;
  currentStep: number;
  onClickStep: (clicked: number) => void;
  disableStepIndicators?: boolean;
}

function StepIndicator({ step, currentStep, onClickStep, disableStepIndicators = false }: StepIndicatorProps) {
  const status = currentStep === step ? "active" : currentStep < step ? "inactive" : "complete";
  const isClickable = step <= currentStep && !disableStepIndicators;

  const handleClick = () => {
    if (isClickable && step !== currentStep) {
      onClickStep(step);
    }
  };

  return (
    <motion.div
      onClick={handleClick}
      className={cn(
        "relative outline-none focus:outline-none",
        isClickable ? "cursor-pointer" : "cursor-not-allowed opacity-60"
      )}
      animate={status}
      initial={false}
    >
      <motion.div
        variants={{
          inactive: { scale: 1, backgroundColor: "var(--border-primmary)", color: "var(--text-muted)" },
          active: { scale: 1, backgroundColor: "var(--link-primary)", color: "var(--link-primary)" },
          complete: { scale: 1, backgroundColor: "var(--link-primary)", color: "var(--link-primary)" },
        }}
        transition={{ duration: 0.3 }}
        className='flex h-8 w-8 items-center justify-center rounded-full font-semibold'
      >
        {status === "complete" ? (
          <CheckIcon className='text-text-title-inv h-4 w-4' />
        ) : status === "active" ? (
          <div className='bg-bg-base h-3 w-3 rounded-full' />
        ) : (
          <span className='text-sm'>{step}</span>
        )}
      </motion.div>
    </motion.div>
  );
}

interface StepConnectorProps {
  isComplete: boolean;
}

function StepConnector({ isComplete }: StepConnectorProps) {
  const lineVariants: Variants = {
    incomplete: { width: 0, backgroundColor: "transparent" },
    complete: { width: "100%", backgroundColor: "var(--link-primary)" },
  };

  return (
    <div className='bg-border-primmary relative mx-2 h-0.5 flex-1 overflow-hidden rounded'>
      <motion.div
        className='absolute top-0 left-0 h-full'
        variants={lineVariants}
        initial={false}
        animate={isComplete ? "complete" : "incomplete"}
        transition={{ duration: 0.4 }}
      />
    </div>
  );
}

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...props} fill='none' stroke='currentColor' strokeWidth={2} viewBox='0 0 24 24'>
      <motion.path
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          delay: 0.1,
          type: "tween",
          ease: "easeOut",
          duration: 0.3,
        }}
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M5 13l4 4L19 7'
      />
    </svg>
  );
}

interface SlideTransitionProps {
  children: ReactNode;
  direction: number;
  onHeightReady: (height: number) => void;
}

function SlideTransition({ children, direction, onHeightReady }: SlideTransitionProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (containerRef.current) {
      onHeightReady(containerRef.current.offsetHeight);
    }
  }, [children, onHeightReady]);

  return (
    <motion.div
      ref={containerRef}
      custom={direction}
      variants={stepVariants}
      initial='enter'
      animate='center'
      exit='exit'
      transition={{ duration: 0.4 }}
      style={{ position: "absolute", left: 0, right: 0, top: 0 }}
    >
      {children}
    </motion.div>
  );
}

const stepVariants: Variants = {
  enter: (dir: number) => ({
    x: dir >= 0 ? "-100%" : "100%",
    opacity: 0,
  }),
  center: {
    x: "0%",
    opacity: 1,
  },
  exit: (dir: number) => ({
    x: dir >= 0 ? "50%" : "-50%",
    opacity: 0,
  }),
};

interface StepContentWrapperProps {
  isCompleted: boolean;
  currentStep: number;
  direction: number;
  children: ReactNode;
  className?: string;
}

function StepContentWrapper({
  isCompleted,
  currentStep,
  direction,
  children,
  className = "",
}: StepContentWrapperProps) {
  const [parentHeight, setParentHeight] = useState<number>(0);

  return (
    <motion.div
      style={{ position: "relative", overflow: "hidden" }}
      animate={{ height: isCompleted ? 0 : parentHeight }}
      transition={{ type: "spring", duration: 0.4 }}
      className={className}
    >
      <AnimatePresence initial={false} mode='sync' custom={direction}>
        {!isCompleted && (
          <SlideTransition key={currentStep} direction={direction} onHeightReady={(h) => setParentHeight(h)}>
            {children}
          </SlideTransition>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

interface StepperProps {
  children: ReactNode;
  initialStep?: number;
  onStepChange?: (step: number) => void;
  onFinalStepCompleted?: () => void;
  nextButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  backButtonText?: string;
  nextButtonText?: string;
  disableStepIndicators?: boolean;
  isCurrentStepValid?: () => boolean;
  onNextClick?: () => Promise<boolean>;
}

function Stepper({
  children,
  initialStep = 1,
  onStepChange = () => {},
  onFinalStepCompleted = () => {},
  nextButtonProps = {},
  backButtonText = "Back",
  nextButtonText = "Continue",
  disableStepIndicators = false,
  isCurrentStepValid = () => true,
  onNextClick,
}: StepperProps) {
  const [currentStep, setCurrentStep] = useState<number>(initialStep);
  const [maxStepReached, setMaxStepReached] = useState<number>(initialStep);
  const [direction, setDirection] = useState<number>(0);
  const stepsArray = Children.toArray(children);
  const totalSteps = stepsArray.length;
  const isCompleted = currentStep > totalSteps;
  const isLastStep = currentStep === totalSteps;

  const updateStep = (newStep: number) => {
    setCurrentStep(newStep);
    if (newStep > maxStepReached) {
      setMaxStepReached(newStep);
    }
    if (newStep > totalSteps) {
      onFinalStepCompleted();
    } else {
      onStepChange(newStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setDirection(-1);
      updateStep(currentStep - 1);
    }
  };

  const handleNext = async () => {
    if (onNextClick) {
      const isValid = await onNextClick();
      if (!isValid) return;
    }

    if (!isLastStep && isCurrentStepValid()) {
      setDirection(1);
      updateStep(currentStep + 1);
    }
  };

  const handleComplete = async () => {
    if (onNextClick) {
      const isValid = await onNextClick();
      if (!isValid) return;
    }

    if (isCurrentStepValid()) {
      setDirection(1);
      updateStep(totalSteps + 1);
    }
  };

  return (
    <div className='w-full'>
      <div className='mx-auto w-full'>
        <div className='flex w-full items-center pb-8'>
          {stepsArray.map((_, index) => {
            const stepNumber = index + 1;
            const isNotLastStep = index < totalSteps - 1;
            return (
              <div key={stepNumber} className='flex flex-1 items-center'>
                <StepIndicator
                  step={stepNumber}
                  disableStepIndicators={disableStepIndicators || stepNumber > maxStepReached}
                  currentStep={currentStep}
                  onClickStep={(clicked) => {
                    if (clicked <= maxStepReached) {
                      setDirection(clicked > currentStep ? 1 : -1);
                      updateStep(clicked);
                    }
                  }}
                />
                {isNotLastStep && <StepConnector isComplete={currentStep > stepNumber} />}
              </div>
            );
          })}
        </div>

        <StepContentWrapper isCompleted={isCompleted} currentStep={currentStep} direction={direction} className=''>
          {stepsArray[currentStep - 1]}
        </StepContentWrapper>

        {!isCompleted && (
          <div className='mt-8'>
            <div className={`flex ${currentStep !== 1 ? "justify-between" : "justify-end"}`}>
              {currentStep !== 1 && (
                <Button
                  type='button'
                  onClick={handleBack}
                  variant='ghost'
                  className='text-text-muted hover:text-text-title'
                >
                  {backButtonText}
                </Button>
              )}
              <Button
                type='button'
                onClick={isLastStep ? handleComplete : handleNext}
                disabled={!isCurrentStepValid()}
                className='rounded-full px-8 py-6 font-semibold'
                {...nextButtonProps}
              >
                {isLastStep ? "Submit Application" : nextButtonText}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Main Component
export default function BecomeaSalesAgent() {
  const [loading, setLoading] = useState(false);
  const [states] = useState<StateData[]>(statesData as StateData[]);
  const [lgas, setLgas] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  console.log("selectedFile", selectedFile);
  const {
    handleSubmit,
    formState: { errors },
    register,
    control,
    setValue,
    watch,
    trigger,
  } = useForm<SalesAgentApplicationType>({
    resolver: zodResolver(SalesAgentApplicationSchema),
    mode: "onChange",
  });

  const selectedState = useWatch({ control, name: "state" });

  // Update LGAs when state changes
  useEffect(() => {
    if (selectedState) {
      const state = states.find((s) => s.name === selectedState);
      if (state) {
        setLgas(state.lgas);
        // Reset LGA when state changes
        setValue("lga", "");
      }
    } else {
      setLgas([]);
    }
  }, [selectedState, states, setValue]);

  const router = useRouter();

  const onSubmit = async (data: SalesAgentApplicationType) => {
    setLoading(true);
    try {
      // Log form data to console
      console.log("Sales Agent Application Data:", {
        ...data,
        cv: {
          name: data.cv.name,
          size: data.cv.size,
          type: data.cv.type,
        },
      });

      // Show success toast
      toast.success(
        "Thank you for applying to be a Sales Agent on Onyx. We've received your application and will be in touch."
      );

      // Redirect to homepage after a short delay
      setTimeout(() => {
        router.push("/");
      }, 1500);
    } catch (error) {
      setLoading(false);
      console.error("Error submitting application:", error);
      toast.error("An error occurred while submitting your application. Please try again.");
    }
  };

  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    startTransition(() => {
      setMounted(true);
    });
  }, []);

  // Watch all form fields
  // eslint-disable-next-line react-hooks/incompatible-library
  const formValues = watch();

  // Step validation functions
  const isStep1Valid = () => {
    const { firstName, lastName, age, phoneNumber, email } = formValues;
    return Boolean(
      firstName &&
      !errors.firstName &&
      lastName &&
      !errors.lastName &&
      age &&
      !errors.age &&
      phoneNumber &&
      !errors.phoneNumber &&
      email &&
      !errors.email
    );
  };

  const isStep2Valid = () => {
    const { state, lga, houseAddress } = formValues;
    return Boolean(state && !errors.state && lga && !errors.lga && houseAddress && !errors.houseAddress);
  };

  const isStep3Valid = () => {
    const { cv, applicationReason } = formValues;
    return Boolean(cv && !errors.cv && applicationReason && !errors.applicationReason);
  };

  const [currentStep, setCurrentStep] = useState(1);

  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1:
        return isStep1Valid();
      case 2:
        return isStep2Valid();
      case 3:
        return isStep3Valid();
      default:
        return false;
    }
  };

  const handleNextClick = async () => {
    let fieldsToValidate: (keyof SalesAgentApplicationType)[] = [];

    switch (currentStep) {
      case 1:
        fieldsToValidate = ["firstName", "lastName", "age", "phoneNumber", "email"];
        break;
      case 2:
        fieldsToValidate = ["state", "lga", "houseAddress"];
        break;
      case 3:
        fieldsToValidate = ["cv", "applicationReason"];
        break;
    }

    const result = await trigger(fieldsToValidate);
    return result;
  };

  return (
    <div className='w-full'>
      <div
        className={cn(
          "text-link-primary mx-auto mb-2 flex w-fit items-center gap-1 text-4xl font-medium md:text-5xl lg:text-6xl 2xl:mb-5"
        )}
      >
        <Image
          src={mounted && theme === "dark" ? "/assets/icons/logo-dark.svg" : "/assets/icons/logo-light.svg"}
          alt='Onyx Logo'
          width={82}
          height={82}
          className='mr-2 inline-flex size-12 md:size-14 lg:size-16'
        />
        Onyx <span className='text-link-primary'>.</span>
      </div>

      <h1 className='text-text-title mb-1 text-center text-2xl leading-[120%] font-semibold md:text-4xl'>
        Become a Sales Agent
      </h1>
      <p className='text-text-title/70 mb-8 text-center text-base md:text-lg'>
        Help other people shop on Onyx<span className='text-link-primary'>.</span>
      </p>

      <Stepper
        initialStep={1}
        onStepChange={(step) => setCurrentStep(step)}
        onFinalStepCompleted={handleSubmit(onSubmit)}
        nextButtonProps={{ disabled: loading }}
        isCurrentStepValid={isCurrentStepValid}
        onNextClick={handleNextClick}
      >
        {/* Step 1: Personal Information */}
        <Step>
          <div className='flex flex-col items-start gap-2.5'>
            <Label htmlFor='firstName'>First Name</Label>
            <Input
              id='firstName'
              type='text'
              placeholder='Enter your first name'
              {...register("firstName")}
              aria-invalid={!!errors.firstName}
            />
            {errors.firstName && <p className='text-text-destructive text-xs'>{errors.firstName.message}</p>}
          </div>

          <div className='flex flex-col items-start gap-2.5'>
            <Label htmlFor='lastName'>Last Name</Label>
            <Input
              id='lastName'
              type='text'
              placeholder='Enter your last name'
              {...register("lastName")}
              aria-invalid={!!errors.lastName}
            />
            {errors.lastName && <p className='text-text-destructive text-xs'>{errors.lastName.message}</p>}
          </div>

          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div className='flex flex-col items-start gap-2.5'>
              <Label htmlFor='age'>Age</Label>
              <Input
                id='age'
                type='number'
                placeholder='Enter your age'
                {...register("age", { valueAsNumber: true })}
                aria-invalid={!!errors.age}
              />
              {errors.age && <p className='text-text-destructive text-xs'>{errors.age.message}</p>}
            </div>

            <div className='flex flex-col items-start gap-2.5'>
              <Label htmlFor='phoneNumber'>Phone Number</Label>
              <Input
                id='phoneNumber'
                type='tel'
                placeholder='08012345678'
                {...register("phoneNumber")}
                aria-invalid={!!errors.phoneNumber}
              />
              {errors.phoneNumber && <p className='text-text-destructive text-xs'>{errors.phoneNumber.message}</p>}
            </div>
          </div>

          <div className='flex flex-col items-start gap-2.5'>
            <Label htmlFor='email'>Email Address</Label>
            <Input
              id='email'
              type='email'
              placeholder='your.email@example.com'
              {...register("email")}
              aria-invalid={!!errors.email}
            />
            {errors.email && <p className='text-text-destructive text-xs'>{errors.email.message}</p>}
          </div>
        </Step>

        {/* Step 2: Location */}
        <Step>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div className='flex flex-col items-start gap-2.5'>
              <Label htmlFor='state'>State of Residence</Label>
              <Select
                onValueChange={(value) => setValue("state", value, { shouldValidate: true })}
                value={watch("state") || ""}
              >
                <SelectTrigger
                  className={cn(
                    "h-14 w-full rounded-full lg:h-[60px] xl:h-[48px]",
                    errors.state && "border-border-destructive"
                  )}
                  aria-invalid={!!errors.state}
                >
                  <SelectValue placeholder='Select your state' />
                </SelectTrigger>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem key={state.name} value={state.name}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.state && <p className='text-text-destructive text-xs'>{errors.state.message}</p>}
            </div>

            <div className='flex flex-col items-start gap-2.5'>
              <Label htmlFor='lga'>Local Government Area (LGA)</Label>
              <Select
                onValueChange={(value) => setValue("lga", value, { shouldValidate: true })}
                value={watch("lga") || ""}
                disabled={!selectedState || lgas.length === 0}
              >
                <SelectTrigger
                  className={cn(
                    "h-14 w-full rounded-full lg:h-[60px] xl:h-[48px]",
                    errors.lga && "border-border-destructive"
                  )}
                  aria-invalid={!!errors.lga}
                >
                  <SelectValue placeholder={selectedState ? "Select your LGA" : "Select state first"} />
                </SelectTrigger>
                <SelectContent>
                  {lgas.map((lga) => (
                    <SelectItem key={lga} value={lga}>
                      {lga}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.lga && <p className='text-text-destructive text-xs'>{errors.lga.message}</p>}
            </div>
          </div>

          <div className='flex flex-col items-start gap-2.5'>
            <Label htmlFor='houseAddress'>House Address</Label>
            <Textarea
              id='houseAddress'
              placeholder='Enter your full house address'
              rows={3}
              {...register("houseAddress")}
              aria-invalid={!!errors.houseAddress}
              className={cn(errors.houseAddress && "border-border-destructive")}
            />
            {errors.houseAddress && <p className='text-text-destructive text-xs'>{errors.houseAddress.message}</p>}
          </div>
        </Step>

        {/* Step 3: Professional Information */}
        <Step>
          <div className='flex flex-col items-start gap-2.5'>
            <Label htmlFor='cv'>Upload CV</Label>
            <FileUpload
              id='cv'
              acceptedFormats={[".pdf", ".doc", ".docx"]}
              maxSize={5 * 1024 * 1024}
              onFileChange={(file) => {
                setSelectedFile(file);
                if (file) {
                  setValue("cv", file, { shouldValidate: true });
                }
              }}
              error={errors.cv?.message}
            />
            <p className='text-text-muted text-xs'>Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
          </div>

          <div className='flex flex-col items-start gap-2.5'>
            <Label htmlFor='applicationReason'>Why do you want to be a sales agent on Onyx?</Label>
            <Textarea
              id='applicationReason'
              placeholder='Tell us why you want to become a sales agent...'
              rows={4}
              {...register("applicationReason")}
              aria-invalid={!!errors.applicationReason}
              className={cn(errors.applicationReason && "border-border-destructive")}
            />
            {errors.applicationReason && (
              <p className='text-text-destructive text-xs'>{errors.applicationReason.message}</p>
            )}
          </div>
        </Step>
      </Stepper>
    </div>
  );
}
